import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  getApiKeysBySession,
  logout,
  WidgetApiKeyModel,
} from '../api/magicLinkApi';
import ApiKeyList from './ApiKeyList';
import DynamicSnackbarMessage from './DynamicSnackbarMessage';
import { useSession } from '../contexts/SessionContext';

function KeysPage() {
  const [apiKeys, setApiKeys] = useState<WidgetApiKeyModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error] = useState('');
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { setAuthenticated } = useSession();

  useEffect(() => {
    const loadKeys = async () => {
      try {
        const keys = await getApiKeysBySession();
        setApiKeys(keys);

        // Auto-navigate to last selected key
        const lastSelectedKey = localStorage.getItem('last_selected_api_key');
        if (lastSelectedKey) {
          const matchedKey = keys.find((k) => k.apiKey === lastSelectedKey);
          if (matchedKey) {
            navigate('/keys/detail', { replace: true });
            return;
          }
          // Key no longer exists, clean up
          localStorage.removeItem('last_selected_api_key');
        }

        // If only one key, skip list and go directly to edit form
        if (keys.length === 1 && keys[0]) {
          localStorage.setItem('last_selected_api_key', keys[0].apiKey);
          navigate('/keys/detail', { replace: true });
          return;
        }

        // Multiple keys but exactly one is inactive — jump straight to it
        // so the user can finish configuring it instead of picking from a list.
        const inactiveKeys = keys.filter((k) => !k.isActive);
        if (inactiveKeys.length === 1 && inactiveKeys[0]) {
          localStorage.setItem('last_selected_api_key', inactiveKeys[0].apiKey);
          navigate('/keys/detail', { replace: true });
          return;
        }
      } catch {
        // Session expired or invalid
        setAuthenticated(false);
        navigate('/', { replace: true });
        return;
      }
      enqueueSnackbar(<DynamicSnackbarMessage textKey="SESSION_TIMEOUT_WARNING" />, {
        variant: 'info',
        persist: true,
        key: 'session-timeout-warning',
        preventDuplicate: true,
      });
      setIsLoading(false);
    };

    loadKeys();
  }, []);

  const handleLogout = async () => {
    localStorage.removeItem('last_selected_api_key');
    await logout();
    window.location.href = 'https://www.ppl.cz';
  };

  const handleSelect = (apiKey: WidgetApiKeyModel) => {
    localStorage.setItem('last_selected_api_key', apiKey.apiKey);
    navigate('/keys/detail');
  };

  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress size={48} sx={{ mb: 3 }} />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mx: 'auto', my: { sm: 0, md: 4 }, maxWidth: 1020 }}>
        <ApiKeyList
          apiKeys={apiKeys}
          onSelect={handleSelect}
          onLogout={handleLogout}
          isLoading={false}
          error={error}
        />
      </Box>
    </>
  );
}

export default KeysPage;
